import { Address } from "@models/address/address";
import { Individual } from "@models/individuals/individual";
import { Relationship } from "@models/relationships/relationship";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { Guid } from "@shared/utils/guid.helper";

export class OfficeholderGroupModel {
  // Common
  relationshipIds: string[];
  individualId: string;
  individual: Individual | null = null;
  officeholderName: string;
  previousOfficeholderName: string;
  types: RelationshipType[];
  address: Address;
  previousAddress: Address | null;
  appointmentDate: string | null;
  previousAppointmentDate: string | null;
  cessationDate: string | null;
  previousCessationDate: string | null;
  dob: string;
  birthCity: string;
  birthCountry: string;
  din: string;

  // Current Officeholder Data
  relationships: Relationship[];

  // Previous Officeholder Data
  previousRelationship: Relationship[];

  constructor(data: Partial<OfficeholderGroupModel> = {}) {
    this.relationshipIds = data.relationshipIds ?? [];
    this.individualId = data.individualId ?? '';
    this.individual = data.individual ?? null;
    this.officeholderName = data.officeholderName ?? '';
    this.previousOfficeholderName = data.previousOfficeholderName ?? '';
    this.types = data.types ?? [];
    this.address = data.address ?? new Address();
    this.previousAddress = data.previousAddress ?? null;
    this.appointmentDate = data.appointmentDate ?? null;
    this.previousAppointmentDate = data.previousAppointmentDate ?? null;
    this.cessationDate = data.cessationDate ?? null;
    this.previousCessationDate = data.previousCessationDate ?? null;
    this.dob = data.dob ?? '';
    this.birthCity = data.birthCity ?? '';
    this.birthCountry = data.birthCountry ?? '';
    this.din = data.din ?? '';
    this.relationships = data.relationships ?? [];
    this.previousRelationship = data.previousRelationship ?? [];
  }

  isRelatedOfficeholder(officer: Relationship): boolean {
    return Guid.isValidGuid(officer.relationshipId)
      && this.individualId == officer.individualId
      && !!officer.individualDataOverride
      && !this.types.includes(officer.type)
      && this.officeholderName == officer.individualDataOverride?.fullName
      && this.address.isEqual(officer.individualDataOverride.address)
      && this.appointmentDate == officer.start?.toString()
      && this.cessationDate == officer.end?.toString()
      && this.dob == officer.individualDataOverride.dob?.toString()
      && this.birthCity == officer.individualDataOverride.birthCity
      && this.birthCountry == officer.individualDataOverride.birthCountry;
  }

  addRelationship(officer: Relationship): void {
    this.relationshipIds.push(officer.relationshipId);
    this.types.push(officer.type);
    this.relationships.push(officer);

    this.types.sort();
  }

  isRelatedPreviousOfficeholder(officer: Relationship): boolean {
    return Guid.isValidGuid(officer.relationshipId)
      && this.relationshipIds.includes(officer.relationshipId);
  }

  addPreviousRelationships(officers: Relationship[]): void {
    officers.forEach((officer) => {
      if (this.isRelatedPreviousOfficeholder(officer)) {
        this.previousOfficeholderName = officer.individualDataOverride?.fullName ?? '';
        this.previousAddress = officer.individualDataOverride?.address ?? null;
        this.previousAppointmentDate = officer.start?.toString() ?? null;
        this.previousCessationDate = officer.end?.toString() ?? null;
        this.previousRelationship.push(officer);
      }
    });
  }

  isCeased(): boolean {
    return Boolean(this.cessationDate && this.cessationDate == this.previousCessationDate);
  }

  get showAppointedNotLodged(): boolean {
    return Boolean(
      !this.previousOfficeholderName
      && this.officeholderName
      && this.appointmentDate !== this.previousAppointmentDate
      && !this.cessationDate
    );
  }

  get showNameChanged(): boolean {
    return Boolean(this.previousOfficeholderName && this.previousOfficeholderName !== this.officeholderName);
  }

  get showOfficerCeased(): boolean {
    return this.cessationDate !== this.previousCessationDate
      && this.previousOfficeholderName === this.officeholderName;
  }

  static fromRelationship(officer: Relationship): OfficeholderGroupModel {
    return new OfficeholderGroupModel({
      relationshipIds: [officer.relationshipId],
      individualId: officer.individualId ?? '',
      individual: officer?.individual ?? null,
      officeholderName: officer.individualDataOverride?.fullName,
      types: [officer.type],
      address: officer.individualDataOverride?.address,
      appointmentDate: officer.start?.toString(),
      cessationDate: officer.end?.toString(),
      dob: officer.individualDataOverride?.dob?.toString(),
      birthCity: officer.individualDataOverride?.birthCity,
      birthCountry: officer.individualDataOverride?.birthCountry,
      din: officer.individualDataOverride?.din,
      relationships: [officer]
    });
  }
}
